// Keep existing Google-shaped callbacks so address UI behavior is unchanged.
// REST service requests go to our backend; the SDK is used only for rendering
// and local geometry objects/calculations.
export function installBackendMapServices(maps, request) {
  if (maps.__instaportBackendServices) return;
  // Share only in-flight transport; preserve each caller's callback and do
  // not retain predictions, geocodes or fares after the request finishes.
  const pending = new Map();
  const requestOnce = async (path, query) => {
    const key = JSON.stringify([path, Object.entries(query).sort()]);
    if (pending.has(key)) return pending.get(key);
    const work = Promise.resolve().then(() => request(path, query));
    pending.set(key, work);
    try { return await work; } finally { pending.delete(key); }
  };
  const coordinate = (value) => {
    if (typeof value === 'string') return value;
    const point = value?.toJSON ? value.toJSON() : value;
    if (!point || point.lat === undefined || point.lng === undefined) throw new Error('Invalid map point');
    return `${typeof point.lat === 'function' ? point.lat() : point.lat},${typeof point.lng === 'function' ? point.lng() : point.lng}`;
  };
  const latLng = (point) => point ? new maps.LatLng(point.lat, point.lng) : point;
  const bounds = (value) => value ? new maps.LatLngBounds(latLng(value.southwest), latLng(value.northeast)) : value;
  const rectangle = (value) => {
    if (!value) return undefined;
    if (typeof value === 'string') return value;
    const west = value.west ?? value.getWest?.();
    const south = value.south ?? value.getSouth?.();
    const east = value.east ?? value.getEast?.();
    const north = value.north ?? value.getNorth?.();
    if ([west, south, east, north].every((coordinate) => Number.isFinite(Number(coordinate)))) {
      return `rectangle:${south},${west}|${north},${east}`;
    }
    return undefined;
  };
  const geocodeResult = (result) => ({ ...result, geometry: {
    ...result.geometry, location: latLng(result.geometry?.location),
    viewport: bounds(result.geometry?.viewport), bounds: bounds(result.geometry?.bounds),
  } });
  const invoke = async (path, query, callback, convert, property) => {
    try {
      const data = await requestOnce(path, query);
      const status = data.status || 'UNKNOWN_ERROR';
      const value = convert(data);
      if (callback) { callback(value, status); return undefined; }
      if (status !== 'OK' && status !== 'ZERO_RESULTS') throw new Error(status);
      return property ? { [property]: value } : value;
    } catch (error) {
      if (callback) { callback(null, 'UNKNOWN_ERROR'); return undefined; }
      throw error;
    }
  };
  maps.Geocoder = class {
    geocode(input, callback) {
      const query = {};
      if (input.address) query.address = input.address;
      if (input.placeId) query.place_id = input.placeId;
      if (input.location) query.latlng = coordinate(input.location);
      if (input.region) query.region = input.region;
      return invoke('/maps/geocode', query, callback,
        (data) => (data.results || []).map(geocodeResult), 'results');
    }
  };
  maps.places.AutocompleteService = class {
    getPlacePredictions(input, callback) {
      const query = { input: input.input };
      const country = input.componentRestrictions?.country;
      if (country) query.components = (Array.isArray(country) ? country : [country]).map((c) => `country:${c}`).join('|');
      if (input.location) query.location = coordinate(input.location);
      if (input.origin) query.origin = coordinate(input.origin);
      if (input.radius !== undefined) query.radius = String(input.radius);
      const locationRestriction = rectangle(input.locationRestriction);
      if (locationRestriction) query.locationrestriction = locationRestriction;
      if (input.types?.length) query.types = input.types.join('|');
      return invoke('/maps/places/autocomplete', query, callback,
        (data) => data.predictions || [], 'predictions');
    }
  };
  maps.DirectionsService = class {
    route(input, callback) {
      const query = { origin: coordinate(input.origin), destination: coordinate(input.destination),
        mode: String(input.travelMode || 'DRIVING').toLowerCase() };
      if (input.waypoints?.length) query.waypoints =
        (input.optimizeWaypoints ? 'optimize:true|' : '') + input.waypoints.map((w) => coordinate(w.location)).join('|');
      return invoke('/maps/directions', query, callback, (data) => ({
        ...data,
        routes: (data.routes || []).map((route) => ({ ...route,
          bounds: bounds(route.bounds),
          overview_path: maps.geometry.encoding.decodePath(route.overview_polyline?.points || ''),
          legs: (route.legs || []).map((leg) => ({ ...leg,
            start_location: latLng(leg.start_location), end_location: latLng(leg.end_location),
          })),
        })),
      }));
    }
  };
  maps.__instaportBackendServices = true;
}
